<template>
  <div class="coupon-page">
    <Card>
      <div class="toolbar">
        <Button type="primary" @click="openCreate">新建券模板</Button>
        <Button class="m-l-10" @click="openGrant">发放给用户</Button>
        <Button class="m-l-10" @click="goIssued">已发放记录</Button>
        <Button class="m-l-10" @click="load">刷新</Button>
      </div>
      <Table class="m-t-16" :loading="loading" :columns="columns" :data="list" border stripe />
    </Card>

    <Modal v-model="showForm" :title="form._id ? '编辑模板' : '新建模板'" @on-ok="saveForm" @on-cancel="resetForm">
      <Form :label-width="100">
        <FormItem label="名称">
          <Input v-model="form.name" placeholder="展示名称" />
        </FormItem>
        <FormItem label="类型">
          <Select v-model="form.biz_type" style="width: 200px">
            <Option value="mall">购物</Option>
            <Option value="car">用车</Option>
          </Select>
        </FormItem>
        <FormItem label="减免(分)">
          <InputNumber v-model="form.discount_fen" :min="1" style="width: 200px" />
          <span class="hint">100 分 = 1 元</span>
        </FormItem>
        <FormItem label="门槛(分)">
          <InputNumber v-model="form.threshold_fen" :min="0" style="width: 200px" />
        </FormItem>
        <FormItem label="有效天数">
          <InputNumber v-model="form.days_valid" :min="1" style="width: 200px" />
        </FormItem>
        <FormItem label="库存">
          <Input v-model.number="form.stock" placeholder="-1 表示不限" />
        </FormItem>
        <FormItem label="每人限领">
          <InputNumber v-model="form.per_user_limit" :min="1" style="width: 200px" />
        </FormItem>
        <FormItem label="首页可领">
          <i-switch v-model="form.popup_enabled" />
        </FormItem>
        <FormItem label="状态">
          <Select v-model="form.status" style="width: 200px">
            <Option value="on">上架</Option>
            <Option value="off">下架</Option>
          </Select>
        </FormItem>
        <FormItem label="备注">
          <Input v-model="form.remark" type="textarea" :rows="2" />
        </FormItem>
        <Divider orientation="left">小程序「我的优惠券」展示</Divider>
        <FormItem label="适用范围文案">
          <Input v-model="form.mini_scope_text" placeholder="卡片左上小字，如：限抵扣会员费" />
        </FormItem>
        <FormItem label="左侧背景色">
          <Select v-model="form.mini_left_bg" style="width: 320px" transfer>
            <Option
              v-for="p in leftBgSelectOptions"
              :key="'l-' + p.value"
              :value="p.value"
              :label="p.label"
            >
              <span class="color-opt">
                <span class="color-dot" :style="colorDotStyle(p.value, true)"></span>
                <span>{{ p.label }}</span>
              </span>
            </Option>
          </Select>
        </FormItem>
        <FormItem label="强调色">
          <Select v-model="form.mini_accent_color" style="width: 320px" transfer>
            <Option
              v-for="p in accentSelectOptions"
              :key="'a-' + p.value"
              :value="p.value"
              :label="p.label"
            >
              <span class="color-opt">
                <span class="color-dot" :style="colorDotStyle(p.value, false)"></span>
                <span>{{ p.label }}</span>
              </span>
            </Option>
          </Select>
        </FormItem>
        <FormItem label="按钮文案">
          <Input v-model="form.mini_button_text" placeholder="默认：去使用" />
        </FormItem>
      </Form>
    </Modal>

    <Modal
      v-model="showGrant"
      title="发放优惠券"
      width="920"
      :mask-closable="false"
      :loading="grantSubmitting"
      @on-ok="doGrant"
      @on-cancel="resetGrant"
    >
      <Form :label-width="88" inline class="grant-form-top">
        <FormItem label="券模板">
          <Select
            v-model="grant.template_id"
            filterable
            clearable
            placeholder="请选择券模板"
            style="width: 320px"
          >
            <Option
              v-for="t in list"
              :key="formatTemplateId(t._id)"
              :value="formatTemplateId(t._id)"
            >
              {{ t.name }}（{{ t.biz_type === 'mall' ? '购物' : '用车' }} · 减{{ t.discount_fen }}分）
            </Option>
          </Select>
        </FormItem>
        <FormItem label="用户搜索">
          <Input
            v-model="grantUserKey"
            clearable
            placeholder="昵称 / 手机号"
            style="width: 200px"
            @on-enter="searchGrantUsers"
          />
        </FormItem>
        <FormItem :label-width="12">
          <Button type="primary" @click="searchGrantUsers">查询</Button>
        </FormItem>
      </Form>
      <div class="grant-selected-tip">
        已选择 <strong>{{ grantSelectedCount }}</strong> 位用户（可翻页继续勾选，已选会保留）
      </div>
      <Table
        ref="grantUserTable"
        :columns="grantUserColumns"
        :data="grantUserList"
        :loading="grantUserLoading"
        border
        height="380"
        @on-selection-change="onGrantUserSelect"
      />
      <Page
        class="grant-user-page"
        :total="grantUserTotal"
        :current="grantUserQuery.pageNo"
        :page-size="grantUserQuery.pageSize"
        :page-size-opts="[10, 20, 50]"
        show-sizer
        show-total
        @on-change="onGrantUserPageChange"
        @on-page-size-change="onGrantUserPageSizeChange"
      />
    </Modal>
  </div>
</template>

<script>
import {
  couponTemplateList,
  couponTemplateCreate,
  couponTemplateUpdate,
  couponGrantBatch,
} from '@/api/coupon'
import { getCustomerList } from '@/api/customer'

/** 小程序卡片左侧背景色预设（value 为空表示按券类型默认） */
const LEFT_BG_PRESETS = [
  { value: '', label: '默认（按类型）' },
  { value: '#e8f4ef', label: '浅绿 · 用车' },
  { value: '#f3edff', label: '浅紫 · 购物' },
  { value: '#fff3e0', label: '浅橙' },
  { value: '#fff0f0', label: '浅粉' },
  { value: '#f5f6f8', label: '浅灰' },
]

/** 金额 / 按钮强调色预设 */
const ACCENT_PRESETS = [
  { value: '', label: '默认（按类型）' },
  { value: '#117462', label: '品牌绿 · 用车' },
  { value: '#722ed1', label: '紫色 · 购物' },
  { value: '#ff6b00', label: '活力橙' },
  { value: '#ff4d4f', label: '促销红' },
  { value: '#fa8c16', label: '暖橙' },
]

export default {
  name: 'CouponManage',
  data() {
    return {
      loading: false,
      list: [],
      showForm: false,
      showGrant: false,
      grantSubmitting: false,
      form: this.emptyForm(),
      grant: { template_id: '' },
      grantUserKey: '',
      grantUserList: [],
      grantUserTotal: 0,
      grantUserLoading: false,
      grantSelectedIdSet: {},
      grantUserQuery: { pageNo: 1, pageSize: 20 },
      grantUserColumns: [
        { type: 'selection', width: 56, align: 'center' },
        {
          title: '昵称',
          minWidth: 140,
          render: (h, { row }) => h('span', row.nickName || row.nickname || '-'),
        },
        { title: '手机号', key: 'phone', minWidth: 130 },
        {
          title: '客户ID',
          minWidth: 200,
          ellipsis: true,
          tooltip: true,
          render: (h, { row }) => h('span', this.formatTemplateId(row._id)),
        },
      ],
      columns: [
        {
          title: '模板ID',
          minWidth: 220,
          ellipsis: true,
          tooltip: true,
          render: (h, { row }) => {
            const text = this.formatTemplateId(row._id)
            return h('span', { attrs: { title: text } }, text)
          },
        },
        { title: '名称', key: 'name', minWidth: 140 },
        {
          title: '类型',
          key: 'biz_type',
          width: 90,
          render: (h, { row }) => h('span', row.biz_type === 'mall' ? '购物' : '用车'),
        },
        { title: '减免(分)', key: 'discount_fen', width: 100 },
        { title: '门槛(分)', key: 'threshold_fen', width: 100 },
        { title: '有效天', key: 'days_valid', width: 80 },
        { title: '库存', key: 'stock', width: 80 },
        { title: '已发', key: 'issued', width: 80 },
        { title: '限领/人', key: 'per_user_limit', width: 90 },
        {
          title: '弹窗',
          key: 'popup_enabled',
          width: 70,
          render: (h, { row }) => h('span', row.popup_enabled ? '是' : '否'),
        },
        { title: '状态', key: 'status', width: 80 },
        {
          title: '操作',
          key: '_id',
          width: 120,
          render: (h, { row }) =>
            h(
              'Button',
              {
                props: { type: 'text', size: 'small' },
                on: { click: () => this.edit(row) },
              },
              '编辑'
            ),
        },
      ],
    }
  },
  computed: {
    grantSelectedCount() {
      return Object.keys(this.grantSelectedIdSet).length
    },
    leftBgSelectOptions() {
      return this.mergeColorPreset(LEFT_BG_PRESETS, this.form.mini_left_bg)
    },
    accentSelectOptions() {
      return this.mergeColorPreset(ACCENT_PRESETS, this.form.mini_accent_color)
    },
  },
  mounted() {
    this.load()
  },
  methods: {
    goIssued() {
      this.$router.push({ name: 'coupon-issued' })
    },
    mergeColorPreset(presets, current) {
      const v = (current || '').trim().toLowerCase()
      if (!v) return presets
      const hit = presets.some((p) => (p.value || '').toLowerCase() === v)
      if (hit) return presets
      return [...presets, { value: current.trim(), label: `已保存 ${current.trim()}` }]
    },
    colorDotStyle(hex, isBg) {
      if (!hex) {
        return {
          background: isBg
            ? 'repeating-linear-gradient(45deg, #eee, #eee 4px, #fff 4px, #fff 8px)'
            : '#ccc',
          border: '1px dashed #bbb',
        }
      }
      return { background: hex, border: '1px solid rgba(0,0,0,0.08)' }
    },
    formatTemplateId(id) {
      if (id == null || id === '') return ''
      if (typeof id === 'object' && id.$oid != null) return String(id.$oid)
      return String(id)
    },
    emptyForm() {
      return {
        _id: '',
        name: '',
        biz_type: 'mall',
        discount_fen: 100,
        threshold_fen: 0,
        days_valid: 30,
        stock: -1,
        per_user_limit: 1,
        popup_enabled: false,
        status: 'on',
        remark: '',
        mini_scope_text: '',
        mini_left_bg: '',
        mini_accent_color: '',
        mini_button_text: '',
      }
    },
    async load() {
      this.loading = true
      try {
        const res = await couponTemplateList()
        if (res.data.code === 200) {
          this.list = res.data.data || []
        } else {
          this.$Message.error(res.data.msg || '加载失败')
        }
      } catch (e) {
        this.$Message.error('加载失败')
      } finally {
        this.loading = false
      }
    },
    openCreate() {
      this.form = this.emptyForm()
      this.showForm = true
    },
    edit(row) {
      const mc = row.mini_card && typeof row.mini_card === 'object' ? row.mini_card : {}
      this.form = {
        _id: row._id,
        name: row.name,
        biz_type: row.biz_type,
        discount_fen: row.discount_fen,
        threshold_fen: row.threshold_fen,
        days_valid: row.days_valid,
        stock: row.stock,
        per_user_limit: row.per_user_limit,
        popup_enabled: !!row.popup_enabled,
        status: row.status || 'on',
        remark: row.remark || '',
        mini_scope_text: mc.scope_text || '',
        mini_left_bg: mc.left_bg || '',
        mini_accent_color: mc.accent_color || '',
        mini_button_text: mc.button_text || '',
      }
      this.showForm = true
    },
    resetForm() {
      this.form = this.emptyForm()
    },
    async saveForm() {
      try {
        if (!this.form.name) {
          this.$Message.warning('请填写名称')
          return
        }
        const mini_card = {
          scope_text: (this.form.mini_scope_text || '').trim(),
          left_bg: (this.form.mini_left_bg || '').trim(),
          accent_color: (this.form.mini_accent_color || '').trim(),
          button_text: (this.form.mini_button_text || '').trim(),
        }
        const payload = {
          name: this.form.name,
          biz_type: this.form.biz_type,
          discount_fen: this.form.discount_fen,
          threshold_fen: this.form.threshold_fen,
          days_valid: this.form.days_valid,
          stock: this.form.stock,
          per_user_limit: this.form.per_user_limit,
          popup_enabled: this.form.popup_enabled,
          status: this.form.status,
          remark: this.form.remark,
          mini_card,
        }
        if (this.form._id) {
          const res = await couponTemplateUpdate(this.form._id, payload)
          if (res.data.code === 200) {
            this.$Message.success('已保存')
            this.showForm = false
            this.load()
          } else this.$Message.error(res.data.msg)
        } else {
          const res = await couponTemplateCreate(payload)
          if (res.data.code === 200) {
            this.$Message.success('已创建')
            this.showForm = false
            this.load()
          } else this.$Message.error(res.data.msg)
        }
      } catch (e) {
        this.$Message.error('保存失败')
      }
    },
    resetGrant() {
      this.grant = { template_id: '' }
      this.grantUserKey = ''
      this.grantUserList = []
      this.grantUserTotal = 0
      this.grantSelectedIdSet = {}
      this.grantUserQuery = { pageNo: 1, pageSize: 20 }
    },
    openGrant() {
      this.resetGrant()
      this.showGrant = true
      this.loadGrantUsers()
    },
    searchGrantUsers() {
      this.grantUserQuery.pageNo = 1
      this.loadGrantUsers()
    },
    onGrantUserPageChange(page) {
      this.grantUserQuery.pageNo = page
      this.loadGrantUsers()
    },
    onGrantUserPageSizeChange(size) {
      this.grantUserQuery.pageSize = size
      this.grantUserQuery.pageNo = 1
      this.loadGrantUsers()
    },
    onGrantUserSelect(selection) {
      const pageIds = this.grantUserList.map((r) => String(r._id))
      const next = { ...this.grantSelectedIdSet }
      pageIds.forEach((id) => {
        delete next[id]
      })
      ;(selection || []).forEach((row) => {
        const id = String(row._id)
        next[id] = true
      })
      this.grantSelectedIdSet = next
    },
    async loadGrantUsers() {
      this.grantUserLoading = true
      try {
        const res = await getCustomerList({
          pageNo: this.grantUserQuery.pageNo,
          pageSize: this.grantUserQuery.pageSize,
          key: (this.grantUserKey || '').trim(),
        })
        if (res.data.code === 200) {
          const rows = res.data.data || []
          this.grantUserList = rows.map((r) => ({
            ...r,
            _id: String(r._id),
            _checked: !!this.grantSelectedIdSet[String(r._id)],
          }))
          this.grantUserTotal = res.data.total || 0
        } else {
          this.$Message.error(res.data.msg || '加载用户失败')
        }
      } catch (e) {
        this.$Message.error('加载用户失败')
      } finally {
        this.grantUserLoading = false
      }
    },
    async doGrant() {
      if (!this.grant.template_id) {
        this.$Message.warning('请选择券模板')
        return false
      }
      if (this.grantSelectedCount < 1) {
        this.$Message.warning('请勾选至少一位用户')
        return false
      }
      this.grantSubmitting = true
      try {
        const res = await couponGrantBatch({
          template_id: this.grant.template_id,
          customer_ids: Object.keys(this.grantSelectedIdSet),
        })
        if (res.data.code === 200) {
          const d = res.data.data || {}
          if (d.failed > 0) {
            this.$Modal.warning({
              title: '部分发放失败',
              content: res.data.msg,
            })
          } else {
            this.$Message.success(res.data.msg || '发放成功')
          }
          this.showGrant = false
          this.resetGrant()
          this.load()
        } else {
          this.$Message.error(res.data.msg || '发放失败')
        }
      } catch (e) {
        this.$Message.error('发放失败')
      } finally {
        this.grantSubmitting = false
      }
    },
  },
}
</script>

<style scoped>
.toolbar {
  display: flex;
  align-items: center;
}
.m-l-10 {
  margin-left: 10px;
}
.m-t-16 {
  margin-top: 16px;
}
.hint {
  margin-left: 8px;
  color: #999;
  font-size: 12px;
}
.color-opt {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.color-dot {
  width: 16px;
  height: 16px;
  border-radius: 4px;
  flex-shrink: 0;
}
.grant-form-top {
  margin-bottom: 8px;
}
.grant-selected-tip {
  margin: 8px 0 12px;
  font-size: 13px;
  color: #666;
}
.grant-user-page {
  margin-top: 12px;
  text-align: right;
}
</style>
